import { Injectable, Output, EventEmitter, Directive } from '@angular/core';
import {Subscription} from 'rxjs';

import {MessageGroup, ChatMessage} from '../modules/live-chat/live-chat.models';
import {MessagingService} from './messaging.service';
import {TdoUserConfigurationModel} from '../models/configuration/user/tdo-user-configuration.model';
import {UserService} from './user.service';

@Injectable({
  providedIn: 'root'
})
export class LiveChatService {
  @Output() getMessageGroupsEvent: EventEmitter<MessageGroup[]> = new EventEmitter<MessageGroup[]>();

  currentUser: any;
  messageGroups: MessageGroup[] = [];
  messagingSubscription: Subscription;

  constructor(
    private messagingService: MessagingService,
    private userService: UserService,
  ) {
    this.init();
    this.loadLocalChatGroups();
  }

  initUser() {
    this.currentUser = JSON.parse(localStorage.getItem('storedUser'));
  }

  initAllChat() {
    const flag: boolean = this.messageGroups.findIndex(group => group.groupId === 'all_chat') > -1;

    if (!flag) {
      const allChatGroup = new MessageGroup();
      allChatGroup.groupId = 'all_chat';
      allChatGroup.groupName = 'Office';
      allChatGroup.lastMessage = new Date();
      allChatGroup.messages = [];
      this.messageGroups.unshift(allChatGroup);
    }
  }

  init() {
    this.initUser();
    this.close();
    this.initAllChat();

    this.generateUserGroups();
    this.getUnreadMessageCount();

    this.messagingSubscription = this.messagingService.componentMessageCall
      .subscribe(response => {

        const event = response.event;
        const eventData = response.data;

        if (event === 'SOCKET_CHAT_EVENT') {
          this.addChatMessage(eventData);
        }
      });
  }

  getUnreadMessageCount() {
    let count = 0;
    this.messageGroups.forEach(group => {
      const groupCount = group.messages.filter(message => !message.isRead).length;
      count += groupCount;
    });

    this.messagingService.sendComponentMessage('ALERT_MESSAGES_COUNT', {count});
  }

  async generateUserGroups() {
    // get all users
    // create groups for those users
    // skip your current user
    // use the userid_otheruserid convention

    try {
      let users: TdoUserConfigurationModel[];

      if (this.userService.users) {
        users = this.userService.users;
      } else {
        users = await this.userService.getUsers(this.currentUser.TDOCID).toPromise();
      }

      users = users.filter(user => user.UserID.toLowerCase() !== this.currentUser.authdata.userid.toLowerCase());

      let newGroupBuilderObjs = users.map(user => {
        const groupId = `${this.currentUser.authdata.userid.toLowerCase()}_${user.UserID.toLowerCase()}`;
        const groupName = `${user.FirstName} ${user.LastName}`;
        return {groupId, groupName};
      });

      newGroupBuilderObjs = newGroupBuilderObjs.filter(builderObj => {

        const groupIndex = this.messageGroups.findIndex(group => {
          if (this.doesGroupIdMatch(group.groupId, builderObj.groupId)) {
            return true;
          }
          return false;
        });

        if (groupIndex === -1) {
          this.createMessageGroup(builderObj.groupId, builderObj.groupName);
        }
      });
    } catch (err) {
      console.log('err', err);
    }
  }

  close() {
    if (this.messagingSubscription && !this.messagingSubscription.closed) {
      this.messagingSubscription.unsubscribe();
    }
  }

  createMessageGroup(groupId: string, groupName: string) {
    const messageGroup = new MessageGroup();
    messageGroup.groupId = groupId;
    messageGroup.groupName = groupName;
    messageGroup.lastMessage = new Date();
    messageGroup.messages = [];
    this.messageGroups.push(messageGroup);
  }

  doesGroupIdMatch(messageGroupId: string, groupGroupId: string) {

    if (messageGroupId === groupGroupId) {
      return true;
    }

    const messageParts = messageGroupId.split('_');
    const groupParts = groupGroupId.split('_');

    let flag: boolean = false;

    messageParts.forEach(part => {
      const foundIndex = groupParts.findIndex(groupPart => groupPart === part) > -1;
      if (!foundIndex) {
        flag = true;
      }
    });

    return !flag;
  }

  shouldDropMessage(chatMessage: ChatMessage) {
    if (chatMessage.groupId === 'all_chat') {
      return false;
    }

    if (chatMessage.groupId.indexOf(this.currentUser.authdata.userid.toLowerCase()) > -1) {
      return false;
    }
    return true;
  }

  addChatMessage(chatMessage: ChatMessage) {

    if (this.shouldDropMessage(chatMessage)) {
      return false;
    }

    chatMessage.isRead = false;
    let added: boolean = false;
    this.messageGroups.forEach(messageGroup => {
      if (this.doesGroupIdMatch(chatMessage.groupId, messageGroup.groupId)) {
        messageGroup.messages.push(chatMessage);
        added = true;
      }
    });

    if (!added) {
      const messageGroup = new MessageGroup();
      messageGroup.groupId = chatMessage.groupId;
      messageGroup.groupName = chatMessage.fromUsername;
      messageGroup.lastMessage = new Date();
      messageGroup.messages = [chatMessage];

      this.messageGroups.push(messageGroup);
    }

    this.trimDownMessageGroups();
    this.saveChatGroupsLocally();
    this.emitMessageGroups();
    this.getUnreadMessageCount();
  }

  loadLocalChatGroups() {
    const storageItemString = localStorage.getItem('Live_chat_items');
    if (storageItemString) {
      this.messageGroups = JSON.parse(storageItemString);
    }
  }

  saveChatGroupsLocally() {
    localStorage.setItem('Live_chat_items', JSON.stringify(this.messageGroups));
  }

  deleteChatGroupsLocally() {
    localStorage.removeItem('Live_chat_items');
    this.messageGroups = [];
    this.emitMessageGroups();
  }

  getMessageGroups() {
    this.emitMessageGroups();
  }

  trimDownMessageGroups() {
    const limit = 150;
    this.messageGroups.forEach(messageGroup => {
      const length = messageGroup.messages.length;
      if (length > limit) {
        messageGroup.messages.splice(0, length - limit);
      }
    });
  }

  sendMessage(message: ChatMessage) {
    message.fromId = this.currentUser.authdata.userid.toLowerCase();
    message.fromUsername = this.currentUser.authdata.userfullname;
    this.addChatMessage(message);
    this.messagingService.sendSocketMessage('SOCKET_CHAT_EVENT', message);
  }

  getCurrentUserId() {
    return this.currentUser.authdata.userid.toLowerCase();
  }

  private emitMessageGroups() {
    this.getMessageGroupsEvent.emit(this.messageGroups);
  }
}
